import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Settings as SettingsIcon, Loader2, Store, Truck } from "lucide-react";

export default function AdminSettings() {
  usePageMeta("Store Settings");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm(data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        store_name: form.store_name,
        whatsapp_number: form.whatsapp_number,
        email: form.email,
        free_delivery_threshold: parseFloat(form.free_delivery_threshold || 0),
        default_delivery_charge: parseFloat(form.default_delivery_charge || 0),
        announcement: form.announcement || "",
      });
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form)
    return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  return (
    <div className="max-w-2xl">
      <h1 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Store Settings</h1>
      <p className="mt-1 text-ink-soft">Business details and global delivery rules.</p>

      <section className="mt-6 rounded-2xl border border-border/60 bg-white p-6 shadow-ambient">
        <h2 className="flex items-center gap-2 font-heading text-lg text-ink"><Store className="h-4 w-4 text-brand" /> Business</h2>
        <div className="mt-4 space-y-4">
          <div>
            <Label>Store name</Label>
            <Input value={form.store_name} onChange={set("store_name")} className="mt-1.5 rounded-xl" data-testid="settings-store-name" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label>WhatsApp number (with country code)</Label>
              <Input value={form.whatsapp_number} onChange={set("whatsapp_number")} className="mt-1.5 rounded-xl" placeholder="917995028255" data-testid="settings-whatsapp" />
            </div>
            <div>
              <Label>Contact email</Label>
              <Input value={form.email} onChange={set("email")} className="mt-1.5 rounded-xl" data-testid="settings-email" />
            </div>
          </div>
          <div>
            <Label>Announcement (optional)</Label>
            <Textarea value={form.announcement} onChange={set("announcement")} className="mt-1.5 rounded-xl" rows={2} placeholder="Shown as a store-wide note" />
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-2xl border border-border/60 bg-white p-6 shadow-ambient">
        <h2 className="flex items-center gap-2 font-heading text-lg text-ink"><Truck className="h-4 w-4 text-brand" /> Delivery</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>Default delivery charge (₹)</Label>
            <Input type="number" value={form.default_delivery_charge} onChange={set("default_delivery_charge")} className="mt-1.5 rounded-xl" data-testid="settings-default-charge" />
            <p className="mt-1 text-xs text-ink-muted">Used when no delivery area is selected.</p>
          </div>
          <div>
            <Label>Free delivery above (₹)</Label>
            <Input type="number" value={form.free_delivery_threshold} onChange={set("free_delivery_threshold")} className="mt-1.5 rounded-xl" data-testid="settings-free-threshold" />
            <p className="mt-1 text-xs text-ink-muted">Set 0 to disable free delivery.</p>
          </div>
        </div>
      </section>

      <Button onClick={save} disabled={saving} className="mt-6 rounded-full bg-brand px-8 text-ink hover:bg-brand-hover" data-testid="save-settings-btn">
        {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</> : <><SettingsIcon className="mr-2 h-4 w-4" /> Save Settings</>}
      </Button>
    </div>
  );
}
